import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken =
  'pk.eyJ1Ijoibmlrb2xhMDgwMyIsImEiOiJjamh4dWJkZnEwZnBhM3Fwa2M1cXhtMmQ1In0.6ghqFS-ZXXTGbgv1oDIzNg';

export default function ProviderProfile() {
  const { id } = useParams<{ id: string }>();
  const [provider, setProvider] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews' | 'photos'>('overview');
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [claimError, setClaimError] = useState('');

  const [claimForm, setClaimForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    document: null as File | null,
  });

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  // Fetch provider data
  useEffect(() => {
    if (!id) return;
    axios
      .get(`https://therevulist.com/wp-json/directorist/v1/listings/${id}`)
      .then((res) => setProvider(res.data))
      .catch((err) => console.error('Error fetching provider:', err));
  }, [id]);

  // Initialize Mapbox
  useEffect(() => {
    if (!provider || !provider.latitude || !provider.longitude) return;

    const lat = Number(provider.latitude);
    const lng = Number(provider.longitude);

    if (mapRef.current) return;

    requestAnimationFrame(() => {
      if (!mapContainerRef.current) return;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [lng, lat],
        zoom: 13,
      });

      new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map);
      mapRef.current = map;
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [provider]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setClaimForm({ ...claimForm, document: e.target.files[0] });
    }
  };

  const submitClaim = async () => {
    if (!claimForm.name || !claimForm.email || !claimForm.document) {
      setClaimError('Name, email, and document are required');
      return;
    }

    const formData = new FormData();
    formData.append('listing_id', id!);
    formData.append('name', claimForm.name);
    formData.append('email', claimForm.email);
    if (claimForm.phone) formData.append('phone', claimForm.phone);
    if (claimForm.message) formData.append('message', claimForm.message);
    formData.append('document', claimForm.document);

    try {
      const res = await axios.post(
        'https://therevulist.com/wp-json/custom/v1/claim',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (res.data.success) {
        setClaimSubmitted(true);
        setClaimError('');
        setShowClaimForm(false);
      } else {
        setClaimError(res.data.error || 'Unknown error');
      }
    } catch (err: any) {
      console.error(err);
      setClaimError(err.response?.data?.error || err.message);
    }
  };

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  const featuredImage =
    provider.images && provider.images.length > 0
      ? provider.images[0].src
      : 'https://via.placeholder.com/400x500?text=No+Image';

  const photos = provider.images?.slice(1) || [];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Provider Header */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Provider Image */}
            <div className="relative">
              <div className="w-48 h-48 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src={featuredImage}
                  alt={provider.name || 'Provider'}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Provider Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-4xl font-bold mb-2">{provider.name || 'Unnamed Provider'}</h1>

                  <div className="flex items-center gap-4 mb-4">
                    {provider.categories?.map((cat: any) => (
                      <span
                        key={cat.id}
                        className="px-4 py-1 bg-teal-500 rounded-full text-sm font-semibold whitespace-nowrap"
                      >
                        {cat.name}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg font-semibold">{provider.average_rating || 0}</span>
                    <span className="text-teal-100">({provider.rating_count || 0} reviews)</span>
                  </div>
                  <p className="text-teal-50 text-lg">{provider.zip || ''}</p>
                </div>

                {/* Claim Listing Button / Verified Badge */}
                {!claimSubmitted ? (
                  <button
                    onClick={() => setShowClaimForm(!showClaimForm)}
                    className="px-6 py-3 bg-white text-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors duration-300"
                  >
                    <i className="ri-shield-check-line mr-2"></i>
                    Claim This Listing
                  </button>
                ) : (
                  <div className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold flex items-center">
                    <i className="ri-shield-check-line mr-2"></i>
                    Verified
                  </div>
                )}
              </div>

              {/* Claim Form */}
              {showClaimForm && !claimSubmitted && (
                <div className="claim-form mt-4 p-6 bg-white rounded-xl shadow-lg">
                  {claimError && <p className="text-red-600 mb-2">{claimError}</p>}
                  <input
                    type="text"
                    placeholder="Name"
                    value={claimForm.name}
                    onChange={(e) => setClaimForm({ ...claimForm, name: e.target.value })}
                    className="w-full mb-2 p-2 border rounded"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={claimForm.email}
                    onChange={(e) => setClaimForm({ ...claimForm, email: e.target.value })}
                    className="w-full mb-2 p-2 border rounded"
                  />
                  <input
                    type="text"
                    placeholder="Phone"
                    value={claimForm.phone}
                    onChange={(e) => setClaimForm({ ...claimForm, phone: e.target.value })}
                    className="w-full mb-2 p-2 border rounded"
                  />
                  <textarea
                    placeholder="Message"
                    value={claimForm.message}
                    onChange={(e) => setClaimForm({ ...claimForm, message: e.target.value })}
                    className="w-full mb-2 p-2 border rounded"
                  />
                  <input type="file" onChange={handleFileChange} className="mb-2" />
                  <button
                    onClick={submitClaim}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700"
                  >
                    Submit Claim
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 flex-wrap gap-4">
            <div className="flex gap-2">
              {['overview', 'reviews', 'photos'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                    activeTab === tab
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8 px-4">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-8">
            {activeTab === 'overview' && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-4">About</h2>
                <div dangerouslySetInnerHTML={{ __html: provider.description || '' }} />
              </div>
            )}

            {activeTab === 'photos' && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6">Photos</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {photos.map((img: any) => (
                    <div key={img.id} className="h-64 rounded-xl overflow-hidden shadow-md">
                      <img src={img.src} alt={img.alt || ''} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Map */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div ref={mapContainerRef} className="w-full" style={{ height: 300 }}></div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              <div className="space-y-4">
                {provider.address && (
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <div className="font-semibold mb-1">Address</div>
                      <p className="text-gray-600 text-sm">{provider.address}</p>
                    </div>
                  </div>
                )}

                {provider.phone && (
                  <div className="flex items-start">
                    <i className="ri-phone-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <div className="font-semibold mb-1">Phone</div>
                      <a href={`tel:${provider.phone}`} className="text-teal-600 text-sm">{provider.phone}</a>
                    </div>
                  </div>
                )}

                {provider.email && (
                  <div className="flex items-start">
                    <i className="ri-mail-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <div className="font-semibold mb-1">Email</div>
                      <a href={`mailto:${provider.email}`} className="text-teal-600 text-sm">{provider.email}</a>
                    </div>
                  </div>
                )}

                {provider.website && (
                  <div className="flex items-start">
                    <i className="ri-global-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <div className="font-semibold mb-1">Website</div>
                      <a href={provider.website} className="text-teal-600 text-sm">{provider.website}</a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
